'use client'

// Animation
import { motion } from 'framer-motion'

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBolt,
  faBug,
  faCodeCompare,
  faCreditCard,
  faArrowsRotate,
  faLock,
  faRobot,
  faShieldHalved,
  faTag,
} from '@fortawesome/free-solid-svg-icons'

// design-studio
import {
  Container,
  fadeInUp,
  Section,
  SectionHeader,
  spaceGrotesk,
  staggerContainer,
} from 'design-studio'

const USE_CASES = [
  {
    icon: faArrowsRotate,
    title: 'Refactor safely',
    desc: 'Prove your refactored function is behaviorally identical to the original — or see precisely where behavior changes.',
  },
  {
    icon: faBug,
    title: 'Catch bugs before production',
    desc: 'Give CodeLogician the function and it exhaustively maps all inputs that cause unexpected outputs — before a single line ships.',
  },
  {
    icon: faCodeCompare,
    title: 'Equivalence checking',
    desc: 'Compare two implementations for semantic equivalence — useful for migrations, API versioning, and shadow deployments.',
  },
  {
    icon: faShieldHalved,
    title: 'Prove invariants',
    desc: 'Establish formal guarantees that hold across all inputs — account balances, state machine transitions, security properties.',
  },
]

const TILES = [
  {
    icon: faCreditCard,
    name: 'Payment flows',
    tag: 'Charge logic, refunds, idempotency',
  },
  {
    icon: faTag,
    name: 'Pricing logic',
    tag: 'Discounts, tiers, edge conditions',
  },
  {
    icon: faLock,
    name: 'Auth & sessions',
    tag: 'Token expiry, permission boundaries',
  },
  {
    icon: faBolt,
    name: 'Matching engines',
    tag: 'Order books, fairness, settlement',
  },
  {
    icon: faRobot,
    name: 'Agent plans',
    tag: 'Agentic loop invariants, tool safety',
  },
]

export function UseCasesNew() {
  return (
    <Section
      id="usecases-new"
      className="relative overflow-hidden py-16 md:py-24 lg:py-32"
    >
      <Container
        className={`${spaceGrotesk.className} relative z-10 max-w-7xl`}
      >
        <SectionHeader
          pretitle="Use Cases"
          title={
            <>
              What developers <br />
              use it for
            </>
          }
          description="From day-to-day code review to high-stakes system verification."
        />
      </Container>

      <Container
        className={`${spaceGrotesk.className} max-w-8xl relative z-10`}
      >
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px 0px' }}
        >
          {/* Use case strip */}
          <motion.div
            variants={fadeInUp}
            className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-700/60 sm:grid-cols-2 lg:grid-cols-4"
          >
            {USE_CASES.map((uc) => (
              <div
                key={uc.title}
                className="border-slate-700/60 bg-slate-800/40 p-6 backdrop-blur-md transition-colors duration-200 hover:bg-slate-800/70 [&:not(:last-child)]:border-b lg:[&:not(:last-child)]:border-r lg:[&:not(:last-child)]:border-b-0 sm:[&:nth-child(1)]:lg:border-r sm:[&:nth-child(2)]:lg:border-r sm:[&:nth-child(3)]:lg:border-r sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:lg:border-r-0"
              >
                <div className="mb-4 flex size-9 items-center justify-center rounded-lg bg-cyan-500/10">
                  <FontAwesomeIcon
                    icon={uc.icon}
                    className="size-4 text-blue-400"
                  />
                </div>
                <p className="mb-1.5 text-base font-semibold text-slate-200">
                  {uc.title}
                </p>
                <p className="text-sm leading-relaxed font-light text-slate-300">
                  {uc.desc}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Tiles */}
          <motion.div variants={fadeInUp} className="mt-10 mb-10">
            <p className="mb-4 font-mono text-xs tracking-widest text-slate-300 uppercase">
              Built for systems that can&apos;t be wrong
            </p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {TILES.map((tile) => (
                <div
                  key={tile.name}
                  className="rounded-xl border border-slate-700/60 bg-slate-800/30 p-4 backdrop-blur-md transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-500/30 hover:bg-cyan-500/5"
                >
                  <div className="mb-3 flex size-8 items-center justify-center rounded-lg bg-slate-700/60">
                    <FontAwesomeIcon
                      icon={tile.icon}
                      className="size-3.5 text-slate-300"
                    />
                  </div>
                  <p className="text-base font-semibold text-slate-200">
                    {tile.name}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed font-light text-slate-300">
                    {tile.tag}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.p
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px 0px' }}
            className="mx-auto max-w-6xl text-base text-slate-300"
          >
            Explore demos and examples in the{' '}
            <a
              href="https://github.com/imandra-ai/codelogician-demos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
            >
              repo
            </a>
            .
          </motion.p>
        </motion.div>
      </Container>
    </Section>
  )
}
